/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useState} from 'react';
import {ActivateHandle, StripeAddress, StripeCustomer, StripeTaxID} from "../../../../util/interfaces";
import {AppContext} from "../../../misc/appContext";
import {
    addTaxID, deleteTaxID, getSubscriptionCustomer, getSubscriptionTaxIDs, updateSubscriptionCustomer
} from "../../../../util/requestSender";
import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {createErrorMessage} from "../../../../util/utils";
import {PermissionCallback} from "../../../../util/pageChanger";
import PageChangePermission from "../../../misc/pageChangePermission";
import CustomerDetails from "./customerDetails";

const emptyCustomer: StripeCustomer = {
    email: null,
    address: {city: null, country: null, line1: null, line2: null, postal_code: null}, name: null,
    phone: null
}

function CustomerDetailsController(props: {}, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const [customer, setCustomer] = useState<StripeCustomer>(emptyCustomer);
    const [taxIDs, setTaxIDs] = useState<Array<StripeTaxID>>([]);
    const [loadingCustomer, setLoadingCustomer] = useState(false);
    const [loadingTaxIDS, setLoadingTaxIDs] = useState(false);
    const [taxIDValue, setTaxIDValue] = useState('');
    const [active, setActive] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    function onActivate() {
        setActive(true);
        Promise.all([loadCustomer(), loadTaxIDs()]);
    }

    function onClose() {
        setActive(false);
        setCustomer(emptyCustomer);
        setTaxIDs([]);
        setLoadingCustomer(false);
        setLoadingTaxIDs(false);
        setTaxIDValue('');
        setHasChanges(false);
    }

    function onChangeCustomer(key: keyof StripeCustomer) {
        return function(value: string) {
            setCustomer({...customer, [key]: value});
            setHasChanges(true);
        }
    }

    function onChangeCustomerAddress(key: keyof StripeAddress) {
        return function(value: string) {
            setCustomer({...customer, address: {...customer.address, [key]: value}});
            setHasChanges(true);
        }
    }

    function onChangeTaxID(value: string) {
        setTaxIDValue(value);
    }

    function onAddTaxID() {
        const taxID = taxIDValue.trim();
        if (!taxID.length || !account) return;
        setLoadingTaxIDs(true);
        addTaxID({
            data: {account_id: account.id, tax_id: taxID}, callback: (data: ResponseData) => {
                if (data.success) loadTaxIDs().then(() => {
                    showSnackbar('Het BTW-nummer is toegevoegd');
                });
            }
        })
    }

    function onDeleteTaxID(taxID: StripeTaxID) {
        return function() {
            if (!account) return;
            setLoadingTaxIDs(true);
            deleteTaxID({
                account_id: account.id, tax_id: taxID.id, callback: (data: ResponseData) => {
                    if (data.success) loadTaxIDs().then(() => {
                        showSnackbar('Het BTW-nummer is verwijderd')
                    });
                    else {
                        setLoadingTaxIDs(false);
                        showDialog(
                            createErrorMessage(data.message ?? 'Het verwijderen van het BTW-nummer is niet gelukt.'),
                            'Verwijderen mislukt');
                    }
                }
            });
        }
    }

    function onDenyPageChange(id: number, callback: PermissionCallback) {
        onSubmitCustomer().then(() => {
            callback(id, true);
        });
    }

    function onSubmitCustomer() {
        return new Promise<void>((resolve, reject) => {
            if (!account) return;
            setLoadingCustomer(true);
            updateSubscriptionCustomer({
                data: {account_id: account.id, ...customer}, callback: (data: ResponseData) => {
                    setLoadingCustomer(false);
                    if (data.success) {
                        setCustomer(data.customer);
                        setHasChanges(false);
                        showSnackbar('De gegevens zijn bijgewerkt');
                        resolve();
                    } else {
                        showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de gegevens is niet gelukt.'),
                            'Bijwerken mislukt');
                        reject();
                    }
                }
            })
        });
    }

    function loadCustomer() {
        return new Promise<void>((resolve, reject) => {
            if (!account) return reject();
            setLoadingCustomer(true)
            getSubscriptionCustomer({
                account_id: account.id, callback: (data: ResponseData) => {
                    setLoadingCustomer(false);
                    if (data.success) {
                        setCustomer(data.customer);
                        resolve();
                    } else {
                        showDialog(createErrorMessage(data.message ?? 'Het laden van je abonnement is niet gelukt.'),
                            'Laden mislukt');
                        reject();
                    }
                }
            })
        });
    }

    function loadTaxIDs() {
        return new Promise<void>((resolve, reject) => {
            if (!account) return resolve();
            setLoadingTaxIDs(true);
            getSubscriptionTaxIDs({
                account_id: account.id, callback: (data: ResponseData) => {
                    setLoadingTaxIDs(false);
                    if (data.success) {
                        setTaxIDs(data.tax_ids);
                        resolve();
                    } else {
                        showDialog(createErrorMessage(data.message ?? 'Het laden van je abonnement is niet gelukt.'),
                            'Laden mislukt');
                        reject();
                    }
                }
            });
        })
    }

    useEffect(() => {
        if (new URLSearchParams(location.search).has('gegevens')) onActivate();
    }, []);

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <>
            <CustomerDetails onClose={onClose} onChangeCustomer={onChangeCustomer}
                             onChangeCustomerAddress={onChangeCustomerAddress} onChangeTaxID={onChangeTaxID}
                             onAddTaxID={onAddTaxID} onDeleteTaxID={onDeleteTaxID} onSubmitCustomer={onSubmitCustomer}
                             customer={customer} taxIDs={taxIDs} loading={loadingCustomer || loadingTaxIDS}
                             taxIDValue={taxIDValue} active={active} hasChanges={hasChanges}/>
            <PageChangePermission changed={hasChanges} onDeny={onDenyPageChange}/>
        </>
    );
}

export default forwardRef<ActivateHandle, {}>(CustomerDetailsController);